import _ from 'lodash'
import moment from 'moment'
import { memo, useEffect } from 'react'

import { SUPPORTED_FILETYPE } from './active-chat'
import { MessageAttachment } from './message-attachment'
import Tag from './tags'
import { UrlPreview } from './url-preview'

const isUrl = text => {
  try {
    let url = new URL(text)

    return (
      url.hostname.length > 0 &&
      url.pathname.length > 0 &&
      url.origin !== 'null' // The string "null" is intentional here
    )
  } catch (e) {
    return false
  }
}

const findFirstUrlFromText = (text) => {
  const splitedText = text?.split(' ') ?? []
  return splitedText.find((t) => isUrl(t))
}

const withLinks = message => {
  const splitText = message?.split(' ') ?? []

  return splitText.reduce((acc, text, k) => {
    if (isUrl(text)) {
      acc.push(
        <a
          key={`${text}-${k}`}
          style={{
            textDecoration: 'underline'
          }}
          target='_blank'
          href={text}
          rel='noreferrer'
        >
          {text}{' '}
        </a>
      )
    } else {
      acc.push(
        <span key={`${text}-${k}`}>
          {text}{' '}
        </span>
      )
    }

    return acc
  }, [])
}

const Message = ({
  message,
  userIdForAttachment,
  authorName,
  tagsSetting,
  messageOwner,
  chatId,
  patientId,
  clientName,
  displayName,
  getUrlPreview
}) => {
  const messageWithLink = withLinks(message.body)

  useEffect(() => {
    const url = findFirstUrlFromText(message.body)
    if (!url) {
      return
    }
    if (!message.url_preview) {
      getUrlPreview(message.id, url)
    }
  }, [ getUrlPreview, message ])

  const attachments = message.attachments ?? []

  const messageHasAttachments =
    Array.isArray(attachments) &&
    attachments.some(attachment => SUPPORTED_FILETYPE.includes(attachment.mime_type))

  const displayUserName = displayName && messageOwner !== 'my'

  return (
    <div
      id={message.id}
      key={message.id}
      className={`chat-message ${messageOwner}`}
    >
      <div className={`chat-body-wrapper ${messageOwner}`}>
        {displayUserName && <div className='chat-message-user'>{authorName}</div>}
        {messageHasAttachments && (
          <MessageAttachment
            rounded={!displayUserName}
            chatId={chatId}
            currentUserId={userIdForAttachment}
            patientId={patientId.split('/')[1]}
            clientName={clientName}
            attachment={attachments[0]}
          />
        )}
        <div className='chat-message-body'>
          {messageWithLink}
          {message.url_preview && (
            <UrlPreview preview={message.url_preview} />
          )}
          <span className='chat-message-time'>
            {message.sending ? <div className='loader-container'><div className='dot-flashing' /></div> : moment(message.created_at).format('HH:mm')}
          </span>
        </div>

        {message?.tags?.length > 0 && (
          <div className='tags-body'>
            {message?.tags?.map(tag => (
              <Tag
                tag={tag}
                key={tag}
                tagsSetting={tagsSetting}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const checkProps = (prev, next) => {
  return _.isEqual(prev, next)
}

export default memo(Message, checkProps)
